import { Calendar } from "primereact/calendar";
import React from "react";

const PrimereactDateRange = ({
  className,
  onChange,
  startDate,
  endDate,
  disabled,
  dType = "date",
  startDateName = "signupDateFrom",
  endDateName = "signupDateInto",
  startTimeName = "startTimeName",
  endTimeName = "endTimeName",
  min = new Date("2021-01-01"), // Minimum date for start date
  max = new Date("2025-12-31"), // Maximum date for start date
  readOnlyInput = false,
  timeOnly = false,
  startTime,
  endTime,
}) => {
  // Convert time string to Date object for PrimeReact Calendar
  const convertTimeStringToDate = (timeString) => {
    if (!timeString) return null;
    if (timeString instanceof Date) return timeString;

    // If it's a string like "00:00" or "23:59"
    if (typeof timeString === "string") {
      const [hours, minutes] = timeString.split(":");
      const date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
      date.setSeconds(0);
      date.setMilliseconds(0);
      return date;
    }

    return null;
  };

  // Convert Date object to time string for consistency
  const convertDateToTimeString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    if (date instanceof Date) {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    }
    return "";
  };

  // Enhanced onChange handler for time fields
  const handleTimeChange = (e, fieldName) => {
    const selectedDate = e.value;
    const timeString = convertDateToTimeString(selectedDate);

    // Create a synthetic event that matches your expected format
    const syntheticEvent = {
      target: {
        name: fieldName,
        value: timeString, // Return as time string for consistency
      },
    };

    if (onChange) {
      onChange(syntheticEvent, fieldName);
    }
  };

  // Enhanced onChange handler for date fields
  const handleDateChange = (e, fieldName) => {
    const syntheticEvent = {
      target: {
        name: fieldName,
        value: e.value, // Keep as Date object
      },
    };

    if (onChange) {
      onChange(syntheticEvent, fieldName);
    }
  };

  // Convert time values for display
  const displayStartTime = convertTimeStringToDate(startTime);
  const displayEndTime = convertTimeStringToDate(endTime);

  return (
    <div className={`${timeOnly ? "" : "grid grid-cols-2 gap-2"} ${className}`}>
      {/* Start Date */}
      <div
        className={`${
          timeOnly
            ? "grid-cols-6 grid items-center space-y-2 space-x-2"
            : "flex gap-2"
        }`}
      >
        <p className={`${timeOnly ? "col-span-1" : "col-span-1 my-auto"}`}>
          Start:{" "}
        </p>
        <Calendar
          disabled={disabled}
          value={startDate}
          showIcon
          classNameParent="col-span-2 w-full"
          className={`${
            timeOnly ? "col-span-2" : "col-span-2 w-full"
          } border rounded-md h-10 `}
          name={startDateName}
          minDate={min}
          maxDate={max}
          readOnlyInput={readOnlyInput}
          pt={{
            root: {
              className: "pl-2",
            },
          }}
          onChange={(e) => handleDateChange(e, startDateName)}
        />
        {timeOnly && (
          <Calendar
            value={displayStartTime}
            disabled={disabled}
            showIcon
            icon="pi pi-clock"
            timeOnly={timeOnly}
            classNameParent="col-span-2 w-full"
            className={`${
              timeOnly ? "col-span-1" : "col-span-2 w-full"
            } border rounded-md h-10 `}
            name={startTimeName}
            readOnlyInput={readOnlyInput}
            pt={{
              root: {
                className: "pl-2",
              },
            }}
            onChange={(e) => handleTimeChange(e, startTimeName)}
          />
        )}
      </div>
      {/* End Date */}
      <div
        className={`${
          timeOnly
            ? "grid-cols-6 grid items-center space-y-2 space-x-2"
            : "flex gap-2"
        }`}
      >
        <p className="col-span-1 my-auto">End: </p>
        <Calendar
          disabled={disabled}
          value={endDate}
          showIcon
          classNameParent="col-span-2 w-full"
          className={`${
            timeOnly ? "col-span-2" : "col-span-2 w-full"
          } border rounded-md h-10 `}
          name={endDateName}
          readOnlyInput={readOnlyInput}
          pt={{
            root: {
              className: "pl-2 focus-none",
            },
          }}
          minDate={min}
          onChange={(e) => handleDateChange(e, endDateName)}
        />
        {timeOnly && (
          <Calendar
            value={displayEndTime}
            disabled={disabled}
            showIcon
            icon="pi pi-clock"
            timeOnly={timeOnly}
            classNameParent="col-span-2 w-full"
            className={`${
              timeOnly ? "col-span-1" : "col-span-2 w-full"
            } border rounded-md h-10 `}
            name={endTimeName}
            readOnlyInput={readOnlyInput}
            pt={{
              root: {
                className: "pl-2",
              },
            }}
            onChange={(e) => handleTimeChange(e, endTimeName)}
          />
        )}
      </div>
    </div>
  );
};

export default PrimereactDateRange;
